import ChatPositionSnapping from "../util/ChatPositionSnapping";
import { isSafari } from "../util/Misc";
import type Move from "./Move";
import ScrollMonitor from "./ScrollMonitor";
import { attachListener, detachListener, isIOS, log } from "./Utils";
/* eslint-disable class-methods-use-this,no-param-reassign,no-extra-boolean-cast */
/**
 * drag resize class handles the drag and drop of messaging chat window
 */

export default class DragResize {
    className: string;

    styleSaved: string;

    cursor: string;

    pDraggerNow: any;

    touchIdentifier: any;

    static TOUCH_IDENTIFER_UNUSED: any;

    uponMouseDropClosure: any;

    uponMouseDragClosure: any;

    uponMouseOutClosure: any;

    uponMouseDragStartClosure: any;

    uponTouchDragClosure: any;

    uponTouchEndClosure: any;

    uponTouchCancelClosure: any;

    uponTouchStartClosure: any;

    deltaY: any;

    deltaX: any;

    pMax: any;

    pMin: any;

    rootWidth: any;

    rootHeight: any;

    draggerHeight: any;

    draggerWidth: any;

    pDraggerStart: any;

    pStart: any;

    pLast: { X: any; Y: any; };

    touchOffsetY: any;

    touchOffsetX: any;

    clickOffsetY: any;

    clickOffsetX: any;

    touch?: Touch;

    root: HTMLElement;

    dragImage: any;

    obj: any;

    static instance: any;

    static INSTANCE_LABEL: "instDragResize";

    dInputel: any;

    mouseIsDown: boolean;

    timeout: any;

    static o: any;

    static DEFAULT_BORDER_COLOR: string;

    holdTimer: NodeJS.Timeout;

    lastTopbarPos: { X: number, Y:number };

    iFrame: HTMLIFrameElement;

    chatPositionSnapping: ChatPositionSnapping;

    constructor() {
        this.className = "DragResize";
        this.styleSaved = "";
        this.cursor = "default";
        this.pDraggerNow = null;
        this.touchIdentifier = DragResize.TOUCH_IDENTIFER_UNUSED;

        /** @private @type {null} */
        this.uponMouseDropClosure = null;
        /** @private @type {null} */
        this.uponMouseDragClosure = null;
        /** @private @type {null} */
        this.uponMouseOutClosure = null;
        /** @private @type {null} */
        this.uponMouseDragStartClosure = null;
        /** @private @type {null} */
        this.uponTouchDragClosure = null;
        /** @private @type {null} */
        this.uponTouchEndClosure = null;
        /** @private @type {null} */
        this.uponTouchCancelClosure = null;
        /** @private @type {null} */
        this.uponTouchStartClosure = null;
        /** @private @type {number} */
        this.deltaY = null;
        /** @private @type {number} */
        this.deltaX = null;
        /** @private @type {null} */
        this.pMax = null;
        /** @private @type {null} */
        this.pMin = null;
        /** @private @type {number} */
        this.rootWidth = null;
        /** @private @type {number} */
        this.rootHeight = null;
        /** @private @type {number} */
        this.draggerHeight = null;
        /** @private @type {number} */
        this.draggerWidth = null;
        /** @private @type {null} */
        this.pDraggerNow = null;
        /** @private @type {null} */
        this.pDraggerStart = null;
        /** @private @type {null} */
        this.pStart = null;
        /** @private @type {null} */
        this.pLast = null;
        /** @private @type {number} */
        this.touchOffsetY = null;
        /** @private @type {number} */
        this.touchOffsetX = null;
        /** @private @type {number} */
        this.clickOffsetY = null;
        /** @private @type {number} */
        this.clickOffsetX = null;
        /** @private @type {null} */
        this.touch = null;
        /** @private @type {string} */
        this.styleSaved = null;
        /** @private @type {string} */
        this.cursor = null;
        /** @private @type {js.HtmlDom} */
        this.root = null;
        /** @private @type {js.HtmlDom} */
        this.dragImage = null;
        /** @private @type {js.HtmlDom} */
        this.obj = null;
    }

    static WhenDone() {
        if (DragResize.instance != null) DragResize.instance.whenDone();
        DragResize.instance = null;
    }


    _removeEventListener(object, eventName, func) {
        detachListener(object, eventName, func);
    }

    _attachEventListener(object, eventName, func) {
        attachListener(object, eventName, func);
    }

    bindClosures() {
        this.uponTouchStartClosure = this.uponTouchStart.bind(this);
        this.uponTouchCancelClosure = this.uponTouchCancel.bind(this);
        this.uponTouchEndClosure = this.uponTouchEnd.bind(this);
        this.uponTouchDragClosure = this.uponTouchDrag.bind(this);

        this.uponMouseDragStartClosure = this.uponDragStart.bind(this);
        this.uponMouseOutClosure = this.uponMouseOut.bind(this);
        this.uponMouseDragClosure = this.uponDrag.bind(this);
        this.uponMouseDropClosure = this.uponDrop.bind(this);
    }


    init(o: HTMLElement, oRoot: HTMLElement) {
        if (o == null) return;
        /* get the child element to drag.  It will be the "input image" element.
         * We will use this child (called dragImage) to attach the touch and mouse events
         */
        this.dragImage = this.fixDragImage(o);
        this.obj = o;
        this.iFrame = window.parent.document.getElementById("inqChatStage") as HTMLIFrameElement;
        this.chatPositionSnapping = new ChatPositionSnapping(this.iFrame);

        /*  When we close the chat interface, we don't actually destroy */
        /*  the div/img/input nodes that we create on initialization. */
        /*  So, we check the [INSTANCE_LABEL] property here as a flag */
        /*  to see if we've already bound the event handlers.  If we */
        /*  have, then we MUST NOT BIND THEM AGAIN, or we get really */
        /*  strange bugs. */
        if (o[DragResize.INSTANCE_LABEL] != null) {
            log(`o already has property for ${DragResize.INSTANCE_LABEL}: ${o[DragResize.INSTANCE_LABEL]}`);
        }
        else {
            o[DragResize.INSTANCE_LABEL] = this;
            oRoot[DragResize.INSTANCE_LABEL] = this;

            /* trace("class name is " + this.className); */
            this.root = oRoot && oRoot != null ? oRoot : o;

            this.bindClosures();

            // Prevent dragging the parent page when the user touch-drags the chat
            if (isIOS()) {
                this.dragImage.style.touchAction = "none";
            }

            /*  Bind initial event handlers */
            if (this.isSupported("touchstart")) {
                /* trace("binding touchstart to " + (untyped o.tagName) + "#" + (untyped o.id)); */
                this.obj[DragResize.INSTANCE_LABEL] = this;
                this._removeEventListener(this.dragImage, "touchstart", this.uponTouchStartClosure);
                this._attachEventListener(this.dragImage, "touchstart", this.uponTouchStartClosure);
            } else if (this.isSupported("mousedown")) {
                this._removeEventListener(this.dragImage, "mousedown", this.uponMouseDragStartClosure);
                this._attachEventListener(this.dragImage, "mousedown", this.uponMouseDragStartClosure);
            }
        }
    }

    fixDragImage(element) {
        /** @type {Node} */
        /* var par;
        /** @type {number} * /
        var _g1 = 0;
        /** @type {number} * /
        var _g;
         if(element.nodeName.toUpperCase() == "IMG") {
            /* trace("    binding to IMG"); * /
            par = element.parentNode;
            /** @type {Element} * /
            var div = document.createElement("DIV");
            div.id = element.id;
            div.style.cssText = "background-color:#007700; opacity:0.4; " + String(element.style.cssText);
            div.title = element.title;
            par.insertBefore(div,element);

            /* Now fix the image so it fills the entire div * /
            element.style.height = element.style.width = "100%";
            element.style.position = "absolute";
            element.style.top = element.style.left = "0px";
            /* Make the image id different from div * /
            element.id = div.id + "_image";
            par.removeChild(element);
            return div;
        } else */
        if (element.nodeName.toUpperCase() == "DIV") {
            /* We want to replace the "img" tag with "input image"
             * So we:
             * 1) Get list of all images.
             * 2) Get the image that is a direct child of the parent div
             * 		This is important because "getElementsByTagName gets all decendents, not just children
             * 3) replace image with the input image
             */
            /** @type {NodeList} * /
             var images = element.getElementsByTagName("IMG");
             if (images.length > 0) {
				/** @type {Node} * /
				var image = null;
				_g1 = 0;
				_g = images.length;
				while (_g1 < _g) {
					/** @type {number} * /
					var i1 = _g1++;
					if (images[i1].parentNode == element) {
						image = images[i1];
						break;
					}
				}
				if (image != null) {
					par = image.parentNode;
					if (par == element) {
						/** @type {Element} * /
						var dragSafe = document.createElement("INPUT");
						dragSafe.type = "image";
						if(image.src == null || image.src == "") dragSafe.src = MessageWindow.clearImage;/* Make sure that the source has the clear image in it * / else dragSafe.src = image.src;/* Make sure that the source has the clear image in it * /
						dragSafe.style.cssText = String(element.style.cssText) + "; top:0px; left:0px; height: 100%; width: 100%;display:block;padding:0;margin:0;";
						dragSafe.className = "tcChat";
						dragSafe.name = "DragSafe";/* We make the name "DragSafe" so that we can find it during debug * /
						addAriaToDrag(dragSafe);
						par.replaceChild(dragSafe,image);
						return dragSafe;
					}
				}
			} */

            /* Now find the first input element that belongs to the DIV as a direct decendant (child)
             * and we
             */
            /** @type {Element} * /
             var inputEl = null;
             /** @type {NodeList} * /
             var inputs = element.getElementsByTagName("INPUT");
             if (inputs.length > 0) {
				_g1 = 0;
				_g = inputs.length;
				while (_g1 < _g) {
					/** @type {number} * /
					var i = _g1++;
					if (inputs[i].parentNode == element) {
						inputEl = inputs[i];
						break;
					}
				}
				if (inputEl != null && inputEl.name != "DragSafe") {
					inputEl.name = "DragSafe";
					/* We make the name "DragSafe" so that we can find it during debug * /
					inputEl.style.cssText =  "cursor: default; top:0px; left:0px; height: 100%; width: 100%;display: block;padding:0;margin:0;";
					inputEl.className = "tcChat";
					this.dInputel = inputEl;
					addAriaToDrag(inputEl);
				}
			}


             /* According issue RTDEV-7155 input elements on the client's page may be changed.
             * Therefore we need to use parent element instead input element to attach handler.
             * Before this was returning an inputEl.
             */
            this.dInputel = element;
        }

        /**
         * Its a utility function created to remove code duplicity
         * @param el
         */
        /*
        function addAriaToDrag(el) {
            el.setAttribute("aria-hidden", "true");
            el.setAttribute("alt", "");
            el.setAttribute("tabindex", "-1");
        }
        */
        return element;
    }


    /**
     * Method to check if left mouse button has been clicked.
     */
     isLeftClick({ button }: MouseEvent): boolean {
        return button === 0;
    }


    fireDone() {
        this.whenDone();
    }


    fireDoneState() {
        DragResize.instance = this;
        window.setTimeout(DragResize.WhenDone, 1);
    }

    /**
     * We don't want to drag the chat when a customer is pressing top bar buttons,
     * menu item buttons, overflow buttons, etc.
     */
    private shouldIgnoreEvent(event: Event) {
        return (
          this.eventIsFromTopBarButton(event) ||
          this.eventIsFromExtendedTopBarButton(event) ||
          this.eventIsFromMenuButton(event) ||
          this.eventIsFromMenuItem(event)
        );
    }

    private eventIsFromTopBarButton(event: Event) {
        const target = event.target as HTMLElement;
        return target.classList.contains("top-bar-item") && target.classList.contains("icon");
    }

    private eventIsFromExtendedTopBarButton(event: Event) {
        const target = event.target as HTMLElement;
        return (
          target.classList.contains("extended-text") ||
          (target.classList.contains("extended-item") &&
            target.classList.contains("icon")) ||
          target.classList.contains("extended-topbar-button")
        );
    }

    private eventIsFromMenuButton(event: Event) {
        const target = event.target as HTMLElement;
        return target.id === "mainMenuOptions";
    }

    private eventIsFromMenuItem(event: Event) {
        const target = event.target as HTMLElement;
        return target.parentElement?.parentElement?.tagName === "OVERFLOW-ITEM"
    }

    removeTouchEvents() {
        this._removeEventListener(window.document, "touchcancel", this.uponTouchCancelClosure); /*  Remove touch handlers. */
        this._removeEventListener(window.document, "touchend", this.uponTouchEndClosure);
        this._removeEventListener(window.document, "touchmove", this.uponTouchDragClosure);
    }


    removeMouseEvents() {
        this._attachEventListener(this.dragImage, "touchstart", this.uponTouchStartClosure); /*  Restore touch handler */
        this._removeEventListener(window.document, "mousemove", this.uponMouseDragClosure);
        this._removeEventListener(window.parent.document, "mousemove", this.uponMouseDragClosure);
        this._removeEventListener(window.document, "mouseup", this.uponMouseDropClosure);
        this._removeEventListener(window.parent.document, "mouseup", this.uponMouseDropClosure);
    }

    addTouchEvents() {
        this._removeEventListener(this.dragImage, "mousedown", this.uponMouseDragStartClosure);/*  Temp. remove the mouse handler */
        this._attachEventListener(window.document, "touchcancel", this.uponTouchCancelClosure);/*  Bind touch handlers */
        this._attachEventListener(window.document, "touchend", this.uponTouchEndClosure);
    }

    addMouseEvents() {
        this._removeEventListener(this.obj, "touchstart", this.uponTouchStartClosure);/*  Temp. remove the touch handler. */
        this._attachEventListener(window.document, "mouseup", this.uponMouseDropClosure);
        this._attachEventListener(window.parent.document, "mouseup", this.uponMouseDropClosure);
    }


    removeEvents() {
        this.removeMouseEvents();
        /* 	removeTouchEvents(); */
        this.fireDoneState();
    }

    uponTouchCancel(ev) {
        /** @type {Event} */
        if (ev.preventDefault != null) ev.preventDefault();
        return false;
    }

    uponTouchEnd(ev: TouchEvent) {
        if (this.shouldIgnoreEvent(ev)) {
            return;
        }
        
        try {
            window.parent.document.body.style.overflow = "auto";
            clearTimeout(this.holdTimer);
            this.touch = this.getTouchByIdentifer(ev);

            if (this.touch === null) {
                return;
            }

            const pCur = { X: (this.touch.pageX + this.root.offsetLeft) - this.touchOffsetX, Y: (this.touch.pageY + this.root.offsetTop) - this.touchOffsetY };

            if (this.mouseIsDown) {
                ev.preventDefault();
                const iFramePos = this.iFrame.getBoundingClientRect();

                // If drag area is more than 5px then we consider that as drag or else it is touch
                if (ev.type === "touchend" && (Math.abs(this.lastTopbarPos?.X - iFramePos.x) > 5 || Math.abs(this.lastTopbarPos?.Y - iFramePos.y) > 5)) {
                    if (this.isSnapChatPositionEnabled()) {
                        this.chatPositionSnapping.snapChatPosition();
                    } else {
                        this.removeTouchEvents();
                        this.repositionTouch(pCur);
                    }
                }

                this.done();
            }

            this.dInputel.style.cursor = "default";
            this.mouseIsDown = false;
        } catch (e1) {
            log(`ERROR: uponTouchEnd: ${e1}`);
        }
    }


    getTouchByIdentifer(ev): Touch | null {
        try {
            /** @type {Array} */
            var touchList = ev.changedTouches;
            if (touchList == null) return null;
            if (this.touchIdentifier == DragResize.TOUCH_IDENTIFER_UNUSED) return null;
            if (touchList.identifiedTouch == null) {
                /*   identifiedTouch not implemented in safari even though in W3C specification */
                /** @type {number} */
                var _g1 = 0;
                /** @type {number} */
                var _g = touchList.length;
                while (_g1 < _g) {
                    /** @type {number} */
                    var i1 = _g1++;
                    if (touchList[i1].identifier == this.touchIdentifier) return touchList[i1];
                }
                return null;
            }
            return touchList.identifiedTouch(this.touchIdentifier);
        } catch (e) {
            log(`ERROR: getTouchByIdentifer: ${e}`);
        }
        return null;
    }

    done() {
        this.touchIdentifier = DragResize.TOUCH_IDENTIFER_UNUSED;
        window.setTimeout(this.whenDone.bind(this), 1);
    }

    uponDrop(ev: MouseEvent) {
        if (this.shouldIgnoreEvent(ev)) {
            return false;
        }

        if (ev.preventDefault != null) {
            ev.preventDefault();
        }

        var pCur = this.getPosition(ev);
        this.removeEvents();
        if (this.mouseIsDown) {
            if (this.isSnapChatPositionEnabled()) {
                this.chatPositionSnapping.snapChatPosition();
            } else {
                this.done();
                this.reposition(pCur);
            }
        } else {
            DragResize.instance = null;
        }
        this.mouseIsDown = false;
        this.dInputel.style.cursor = "default";
        clearTimeout(this.holdTimer);
        return false;
    }

    uponMouseOut(ev) {
        if (this.shouldIgnoreEvent(ev)) {
            return false;
        }

        /** @type {Event} */
        if (ev.preventDefault != null) {
            ev.preventDefault();
        }

        // var pMax = this.getDefaultMax();
        var pMin = this.getDefaultMin();
        var pMax = this.getDefaultMax();

        /** @type {Object} */
        var pCur = this.getPosition(ev);
        /** @type {boolean} */
        var outOfArea = false;
        /** @type {Object} */


        if (pCur.Y < pMin.Y) {
            pCur.Y = pMin.Y;
            outOfArea = true;
        }
        if (pCur.X < pMin.X) {
            pCur.X = pMin.X;
            outOfArea = true;
        }
        if (pCur.Y >= pMax.Y + this.root.offsetHeight) {
            pCur.Y = pMax.Y + this.root.offsetHeight;
            outOfArea = true;
        }
        if (pCur.X >= pMax.X + this.root.offsetWidth) {
            pCur.X = pMax.X + this.root.offsetWidth;
            outOfArea = true;
        }
        if (outOfArea || !this.mouseIsDown) {
            /* trace ("X,Y = " + pCur.X + "," + pCur.Y); */
            this.removeEvents();
            // this.restoreStyle();
            if (this.mouseIsDown) {
                this.reposition(pCur);
                this.fireDoneState();
            } else {
                DragResize.instance = null;
            }
            this.mouseIsDown = false;
            this.dInputel.style.cursor = "default";
            clearTimeout(this.holdTimer);
            return false;
        }
        /* trace("unexpected out of area X,Y = " + pCur.X + "," + pCur.Y); */
        return this.uponDrag(ev);
    }

    getScreenMax() {
        return { X: ScrollMonitor.getScrollWidth() - 5, Y: ScrollMonitor.getScrollHeight() - 5 };
    }


    repositionTouch(pCur) {
        try {
            /* If no movement since the last check, then return */
            if (this.pLast != null && pCur.X == this.pLast.X && pCur.Y == this.pLast.Y) {
                return false;
            }
            /** @type {number} */
            var top;
            /** @type {number} */
            var left;

            left = pCur.X;
            if (pCur.X != this.pLast.X && left >= this.pMin.X && left < this.pMax.X) this.setLeft(left); else /* if x-axis movement in the valid area then reposition Left */
            if (left >= this.pMax.X) this.setLeft(left = this.pMax.X); else /* if x-axis movement more than maximum, then use maximum */
            if (left < this.pMin.X) this.setLeft(left = this.pMin.X);
            /* if x-axis movement less than minimum, then use minimum */

            top = pCur.Y;
            if (pCur.Y != this.pLast.Y && top >= this.pMin.Y && top < this.pMax.Y) this.setTop(top); else /* if y-axis movement in the valid area then reposition Top */
            if (top >= this.pMax.Y) this.setTop(top = this.pMax.Y); else /* if y-axis movement more than maximum, then use maximum */
            if (top < this.pMin.Y) this.setTop(top = this.pMin.Y);
            /* if y-axis movement less than minimum, then use minimum */


            this.pDraggerNow = { X: left, Y: top };
            this.pLast = pCur;
        } catch (e) {
            log(`ERROR: repositionTouch: ${e}`);
        }
        return false;
    }


    reposition(pCur: { X: number, Y: number }) {
        var top = pCur.Y - this.deltaY;
        var left = pCur.X - this.deltaX;

        if (left >= this.pMin.X && left < this.pMax.X) {
            this.pDraggerNow.X = left;
        } else if (left >= this.pMax.X) {
            this.pDraggerNow.X = this.pMax.X;
        } else if (left < this.pMin.X) {
            this.pDraggerNow.X = this.pMin.X;
        }
        if (top >= this.pMin.Y && top < this.pMax.Y) {
            this.pDraggerNow.Y = top;
        } else if (top >= this.pMax.Y) {
            this.pDraggerNow.Y = this.pMax.Y;
        } else if (top < this.pMin.Y) {
            this.pDraggerNow.Y = this.pMin.Y;
        }

        // after check and fix coordinates we set this coordinates for chat,
        // and also set current position as last.
        this.setTop(this.pDraggerNow.Y);
        this.setLeft(this.pDraggerNow.X);
        this.pLast = pCur;
        return false;
    }


    uponTouchDrag(ev: TouchEvent) {
        if (this.shouldIgnoreEvent(ev)) {
            return false;
        }

        if (ev.preventDefault != null) ev.preventDefault();
        if (ev.stopPropagation != null) ev.stopPropagation();

        try {
            if (!!this.obj.dragging) {
                return false;
            }
            this.touch = this.getTouchByIdentifer(ev);
            if (this.touch == null) {
                return false;
            }

            this.obj.dragging = true;

            var pCur = { X: (this.touch.pageX + this.root.offsetLeft) - this.touchOffsetX, Y: (this.touch.pageY + this.root.offsetTop) - this.touchOffsetY };

            if (this.timeout) {
                window.cancelAnimationFrame(this.timeout);
                this.timeout = null;
            }
            this.timeout = window.requestAnimationFrame(() => {
                // Because this is in a request animation frame, it's possible the mouse button is
                // no longer being held down by the time this gets triggered, and `uponTouchEnd` has
                // already triggered. If the user released the mouse, we should not continue this
                // drag frame.
                if (!this.mouseIsDown) {
                    return;
                }

                this.timeout = null;
                this.repositionTouch(pCur);
            });

            this.obj.dragging = false;
        } catch (e1) {
            log(`uponTouchDrag ERROR: ${e1}`);
        }
        return false;
    }

    uponDrag(ev: MouseEvent | PointerEvent) {
        if (this.shouldIgnoreEvent(ev)) {
            return false;
        }

        if (!!this.obj.dragging) {
            return false;
        }

        this.obj.dragging = true;
        if (ev.preventDefault != null) ev.preventDefault();

        var pCur = ev.currentTarget == window.parent.document ? { X: ev.clientX, Y: ev.clientY } : this.getPosition(ev);

        if (this.timeout) {
            window.cancelAnimationFrame(this.timeout);
            this.timeout = null;
        }

        this.timeout = window.requestAnimationFrame(() => {
            this.timeout = null;

            if (this.mouseIsDown) {
                this.reposition(pCur);
            }
        });

        this.obj.dragging = false;
        return false;
    }


    uponTouchStart(ev: TouchEvent) {
        if (this.shouldIgnoreEvent(ev)) {
            return;
        }

        if (ev.stopPropagation != null) ev.stopPropagation();

        this.lastTopbarPos = { X: this.iFrame.getBoundingClientRect().x, Y: this.iFrame.getBoundingClientRect().y };

        if (this.touchIdentifier != DragResize.TOUCH_IDENTIFER_UNUSED) {
            return;
        }

        this.chatPositionSnapping.onDragStart();

        window.parent.document.body.style.overflow = "hidden";
        this.addTouchEvents();
        clearTimeout(this.holdTimer);
        this.holdTimer = setTimeout(() => {
            this.dInputel.style.cursor = "move";
            this.touch = ev.changedTouches[0];
            this.pLast = {X: this.touch.pageX + this.root.offsetLeft, Y: this.touch.pageY + this.root.offsetTop};
            this.touchOffsetX = this.pLast.X - this.root.offsetLeft;
            this.touchOffsetY = this.pLast.Y - this.root.offsetTop;
            this.touchIdentifier = this.touch.identifier;

            this.pMin = this.getDefaultMin();
            this.pMax = this.getDefaultMax();

            this.mouseIsDown = true;
            this._attachEventListener(window.document, "touchmove", this.uponTouchDragClosure);
        }, 50);
    }


    uponDragStart(ev) {
        if (this.shouldIgnoreEvent(ev)) {
            return false;
        }
        
        /** @type {Event} */
        if (ev.preventDefault != null) {
            ev.preventDefault();
        }

        /** @type {Element} */
        var o = this.obj; // eslint-disable-line no-unused-vars
        var that = this;
        if (!this.isLeftClick(ev)) {
            return false;
        }

        // o.style.cursor = this.cursor;

        this.chatPositionSnapping.onDragStart();
        DragResize.instance = this;

        that.addMouseEvents();
        this.holdTimer = setTimeout(function () {
            that.dInputel.style.cursor = "move";

            that.pDraggerNow = { X: that.root.offsetLeft, Y: that.root.offsetTop };
            that.pLast = that.getPosition(ev);

            that.deltaX = that.pLast.X - that.pDraggerNow.X;
            that.deltaY = that.pLast.Y - that.pDraggerNow.Y;

            that.pMin = that.getDefaultMin();
            that.pMax = that.getDefaultMax();
            that.mouseIsDown = true;
            that._attachEventListener(window.document, "mousemove", that.uponMouseDragClosure);
            that._attachEventListener(window.parent.document, "mousemove", that.uponMouseDragClosure);
        }, 50);


        return false;
    }


    setDragBorder() {
    }

    getDefaultMax() {
        return { X: 10000, Y: 10000 };
    }


    getDefaultMin() {
        return { X: 0, Y: 0 };
    }


    setLeft(top) { // eslint-disable-line no-unused-vars
        log("setLeft override failed");
    }

    setTop(top) { // eslint-disable-line no-unused-vars
        log("setTop override failed");
    }

    whenDone() {
    }


    isSupported(eventType) {
        return "on" + eventType in document.documentElement;
    }

    getPositionIE(e) { // eslint-disable-line no-unused-vars
        const pEvent: any = window.parent.event;
        return { X: pEvent.clientX, Y: pEvent.clientY };
    }


    getPositionGecko(e) {
        return { X: e.clientX, Y: e.clientY };
    }

    getPosition(e) {
        return { X: e.clientX + this.root.offsetLeft, Y: e.clientY + this.root.offsetTop };
    }

    private isSnapChatPositionEnabled() {
        if (this.className === "Move") {
            const moveInstance = (this as unknown) as Move;
            return moveInstance.dm === "horizontal-snap";
        }

        return false;
    }

    clear() {
        this.root[DragResize.INSTANCE_LABEL] = null;
        this.obj[DragResize.INSTANCE_LABEL] = null;
        this._removeEventListener(this.obj, "touchstart", this.uponTouchStartClosure);
        this._removeEventListener(this.dragImage, "mousedown", this.uponMouseDragStartClosure);
    }
}

DragResize.o = null;
DragResize["typeof"] = function (v) {
    return typeof (v);
};


DragResize.TOUCH_IDENTIFER_UNUSED = -1;

/** @private @type {string} */
DragResize.INSTANCE_LABEL = "instDragResize";

/** @type {null} */
DragResize.instance = null;

/** @type {string} */
DragResize.DEFAULT_BORDER_COLOR = "SlateGray";
